/*
 * Santa Maura analytics layer.
 *
 * 1. Lead source. The first and the latest traffic source for each visitor (UTM tags, ad click ids,
 *    referring site), kept in this browser's localStorage. It only leaves the browser when the visitor
 *    sends a quote request, as part of that request, so every quote email says where the buyer came from.
 * 2. Events. The quote funnel and discovery interactions, published to Shopify customer events (any pixel,
 *    including Shopify's own analytics, can subscribe) and to GA4 when a measurement ID is set in theme
 *    settings. GA4 loads only once Shopify's consent API allows analytics for this visitor.
 * 3. Real-visitor Core Web Vitals (LCP, CLS, INP), sent once per page as an event.
 */
(() => {
  const KEY = 'sm_attr_v1';
  const cfg = window.SM_ANALYTICS || {};
  const today = () => new Date().toISOString().slice(0, 10);
  const store = {
    get() { try { return JSON.parse(localStorage.getItem(KEY)) || {}; } catch (e) { return {}; } },
    set(v) { try { localStorage.setItem(KEY, JSON.stringify(v)); } catch (e) { /* private mode */ } },
  };

  /* 1. Lead source */
  const SEARCH = /(^|\.)(google|bing|duckduckgo|yahoo|ecosia|baidu|yandex|search\.brave)\./i;
  const SOCIAL = /(^|\.)(facebook|instagram|linkedin|lnkd|t\.co|twitter|x\.com|tiktok|pinterest|youtube|reddit|nextdoor)\b/i;
  const AI = /(chatgpt|openai|perplexity|claude\.ai|gemini\.google|copilot\.microsoft)/i;

  const touchFromUrl = () => {
    const url = new URL(location.href);
    const q = (k) => (url.searchParams.get(k) || '').slice(0, 80);
    let ref = '';
    try {
      const r = document.referrer && new URL(document.referrer);
      if (r && r.hostname !== location.hostname && !/myshopify\.com$|shopify\.com$/.test(r.hostname)) ref = r.hostname.replace(/^www\./, '');
    } catch (e) { /* bad referrer */ }
    let source = q('utm_source');
    let medium = q('utm_medium');
    if (!source && q('gclid')) { source = 'google'; medium = 'cpc'; }
    if (!source && q('msclkid')) { source = 'bing'; medium = 'cpc'; }
    if (!source && q('fbclid')) { source = 'facebook'; medium = medium || 'social'; }
    if (!source && ref) {
      source = ref;
      medium = AI.test(ref) ? 'ai-assistant' : SEARCH.test(ref) ? 'organic' : SOCIAL.test(ref) ? 'social' : 'referral';
    }
    if (!source) return null;
    return { source, medium: medium || 'referral', campaign: q('utm_campaign'), landing: location.pathname, date: today() };
  };

  const attr = store.get();
  const touch = touchFromUrl();
  if (!attr.first) attr.first = touch || { source: 'direct', medium: 'none', campaign: '', landing: location.pathname, date: today() };
  if (touch) attr.last = touch;
  else if (!attr.last) attr.last = attr.first;
  try {
    if (!sessionStorage.getItem('sm_sess')) {
      sessionStorage.setItem('sm_sess', '1');
      attr.sessions = (attr.sessions || 0) + 1;
    }
  } catch (e) { attr.sessions = attr.sessions || 1; }
  attr.pages = (attr.pages || 0) + 1;
  store.set(attr);

  const describe = (t) => (t ? `${t.source} / ${t.medium}${t.campaign ? ` (${t.campaign})` : ''}, landed on ${t.landing}, ${t.date}` : 'unknown');
  window.SMAttr = {
    get: () => store.get(),
    short: () => { const a = store.get(); return a.first ? `${a.first.source} / ${a.first.medium}` : 'unknown'; },
    summary: () => {
      const a = store.get();
      return [`First visit: ${describe(a.first)}`, `Latest source: ${describe(a.last)}`, `Visits: ${a.sessions || 1}, pages viewed: ${a.pages || 1}`].join('\n');
    },
  };

  /* 2. Events */
  let gaOn = false;
  const pending = [];
  const toGA = (name, params) => {
    if (gaOn) window.gtag('event', name, params);
    else if (cfg.ga4) pending.push([name, params]);
  };
  const track = (name, params = {}) => {
    const p = { ...params, lead_source: window.SMAttr.short() };
    try { if (window.Shopify && Shopify.analytics && Shopify.analytics.publish) Shopify.analytics.publish(name, p); } catch (e) { /* not ready */ }
    toGA(name, p);
  };
  window.SMTrack = track;

  const startGA = () => {
    if (!cfg.ga4 || gaOn) return;
    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag() { window.dataLayer.push(arguments); };
    const s = document.createElement('script');
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(cfg.ga4)}`;
    document.head.appendChild(s);
    window.gtag('js', new Date());
    window.gtag('config', cfg.ga4, { page_type: document.documentElement.dataset.template || '' });
    gaOn = true;
    pending.splice(0).forEach(([n, p]) => window.gtag('event', n, p));
  };
  const analyticsAllowed = () => {
    try { return window.Shopify.customerPrivacy.analyticsProcessingAllowed(); } catch (e) { return false; }
  };
  if (cfg.ga4 && window.Shopify && Shopify.loadFeatures) {
    Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (err) => { if (!err && analyticsAllowed()) startGA(); });
    document.addEventListener('visitorConsentCollected', () => { if (analyticsAllowed()) startGA(); });
  }

  // Discovery and contact interactions (the quote funnel is tracked from sm-quote.js)
  document.addEventListener('click', (e) => {
    const a = e.target.closest('a, button');
    if (!a) return;
    const href = a.getAttribute('href') || '';
    if (href.startsWith('tel:')) track('sm_contact_click', { method: 'phone' });
    else if (href.startsWith('mailto:')) track('sm_contact_click', { method: 'email' });
    else if (/maps\.google\./.test(href)) track('sm_dock_map_click', {});
    else if (a.matches('.sm-chip') && /\/collections\//.test(href)) track('sm_filter_click', { filter: href.split('/').pop().split('?')[0] });
    else if (a.matches('[data-hi-tier]')) track('sm_heat_tier', { tier: a.dataset.hiTier });
    else if (a.matches('[data-hi-open]')) track('sm_heat_profile', { chile: (a.closest('.sm-hi__row') || {}).id || '' });
  }, true);

  /* 3. Core Web Vitals from real visitors */
  const vitals = {};
  const observe = (type, fn, opts = {}) => {
    try { new PerformanceObserver((list) => fn(list.getEntries())).observe({ type, buffered: true, ...opts }); } catch (e) { /* unsupported */ }
  };
  observe('largest-contentful-paint', (es) => { const e = es[es.length - 1]; if (e) vitals.lcp = Math.round(e.startTime); });
  let cls = 0;
  observe('layout-shift', (es) => { es.forEach((e) => { if (!e.hadRecentInput) cls += e.value; }); vitals.cls = Math.round(cls * 1000) / 1000; });
  let inp = 0;
  observe('event', (es) => { es.forEach((e) => { if (e.interactionId) inp = Math.max(inp, e.duration); }); vitals.inp = Math.round(inp); }, { durationThreshold: 40 });
  let vitalsSent = false;
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'hidden' || vitalsSent || vitals.lcp === undefined) return;
    vitalsSent = true;
    track('sm_web_vitals', { ...vitals, page_type: document.documentElement.dataset.template || '' });
  });
})();
